import { useState } from 'react';
import { useGT } from 'gt-react';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';
import {
  PpvReceiveAtSchema,
  type CostConstraintAdjustment,
  type PpvReceiveAt,
} from '@erp/shared-types';
import {
  initialReceiveCostInput,
  receiveCostInputState,
} from '../utils/receiveCostAdjustment';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { toast } from 'src/components/ui/toast';
import {
  useSetLineBuyTerms,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  linesInScope,
  isAdjustableLine,
  type AdjustScope,
} from '../utils/revisedCostAdjust';
import { CostAdjustmentScope } from './CostAdjustmentScope';
import { useLineBulkEditTargets } from './lineBulkEditContext';

type Props = {
  line: AssignmentLine;
  getOrderLines: () => readonly AssignmentLine[];
};

export function ReceiveCostAdjust({
  line,
  getOrderLines,
  disabled,
}: Props & { disabled: boolean }) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={gt('Adjust receive cost', {
            context: 'buyer board receive cost editor button',
          })}
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          <SlidersHorizontalIcon className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-3"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <ReceiveCostForm
          line={line}
          getOrderLines={getOrderLines}
          onDone={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function ReceiveCostForm({
  line,
  getOrderLines,
  onDone,
}: Props & { onDone: () => void }) {
  const gt = useGT();
  const orderLines = getOrderLines();
  const selectedIds = useLineBulkEditTargets()(line.suggestionId);
  const [scope, setScope] = useState<AdjustScope>(
    selectedIds.length > 1 ? 'selected' : 'line',
  );
  const initial = initialReceiveCostInput(line.buyTerms);
  const [basis, setBasis] = useState<PpvReceiveAt>(initial.basis);
  const [kind, setKind] = useState<CostConstraintAdjustment['kind']>(
    initial.kind,
  );
  const [value, setValue] = useState(initial.value);
  const { mutate, isPending } = useSetLineBuyTerms();
  const targets = linesInScope(scope, line, selectedIds, orderLines);
  const { adjustment, valid, preview } = receiveCostInputState(
    line,
    targets,
    basis,
    { kind, value },
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        mutate(
          {
            suggestionIds: targets.map((target) => target.suggestionId),
            terms: { ppvReceiveAt: basis },
            ...(basis === 'cost_constraint'
              ? { receiveCostAdjustment: adjustment }
              : {}),
          },
          {
            onSuccess: onDone,
            onError: () =>
              toast.error(
                gt(
                  'Could not save the receive cost. Refresh the prices and try again.',
                  { context: 'receive cost edit error' },
                ),
              ),
          },
        );
      }}
    >
      <label className="flex flex-col gap-1 text-xs">
        {gt('Receive basis', { context: 'receive cost editor select label' })}
        <select
          className="h-8 rounded border bg-background px-2"
          value={basis}
          onChange={(event) => {
            const result = PpvReceiveAtSchema.safeParse(event.target.value);
            if (result.success) setBasis(result.data);
          }}
        >
          <option value="po_cost">
            {gt('PO cost', { context: 'receive basis option' })}
          </option>
          <option value="buyer_cost">
            {gt('Buyer cost', { context: 'receive basis option' })}
          </option>
          <option value="cost_constraint">
            {gt('Cost constraint cost', {
              context: 'receive basis option between system and PO costs',
            })}
          </option>
        </select>
      </label>
      {basis === 'cost_constraint' && (
        <>
          <label className="flex flex-col gap-1 text-xs">
            {gt('Enter as', { context: 'receive cost entry mode' })}
            <select
              className="h-8 rounded border bg-background px-2"
              value={kind}
              onChange={(event) => {
                if (event.target.value === 'amount') {
                  setKind('amount');
                  setValue(preview ?? '');
                } else {
                  setKind('savings_percent');
                  setValue('50');
                }
              }}
            >
              <option value="amount">
                {gt('Amount', { context: 'receive unit cost entry mode' })}
              </option>
              <option value="savings_percent">
                {gt('% of savings above PO cost', {
                  context:
                    '0 percent means PO cost, 100 percent means buyer cost',
                })}
              </option>
            </select>
          </label>
          <Input
            inputMode="decimal"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-label={gt('Receive cost value', {
              context: 'receive cost amount or savings percentage input',
            })}
            className="h-8 text-right tabular-nums"
          />
          <p className="text-xs text-muted-foreground">
            {gt(
              '50% splits the savings equally. Each receive cost must stay between its system and PO costs.',
              { context: 'receive cost validation explanation' },
            )}
          </p>
          {preview !== null && (
            <p className="text-xs tabular-nums">
              {gt('This line receives at {cost}', {
                context: 'receive cost preview',
                cost: preview,
              })}
            </p>
          )}
          {!valid && (
            <p role="alert" className="text-xs text-destructive">
              {gt(
                'Enter an amount within every selected line’s costs, or a percentage from 0 to 100. Both costs are required.',
                { context: 'invalid receive cost across selected lines' },
              )}
            </p>
          )}
        </>
      )}
      <CostAdjustmentScope
        scope={scope}
        onChange={setScope}
        selectedCount={selectedIds.length}
        orderBuyCount={orderLines.filter(isAdjustableLine).length}
      />
      <Button size="sm" type="submit" disabled={!valid || isPending}>
        <SlidersHorizontalIcon className="size-4" />
        {gt('Apply', { context: 'save receive costs button' })}
      </Button>
    </form>
  );
}
